import { Add } from '@mui/icons-material';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CreateVisitFormValues,
  CreateVisitVisitorFormValues,
  MAX_VISITORS,
} from '../../organisms/create-visit/schema/create-visit-validation-schema';
import VisitorsTable from '../../organisms/create-visit/visitors-table';

import { AddVisitorDiscountDialog } from './add-visitor-discount-dialog';
import { ImportVisitorsDialogButton } from './import-visitors-dialog-button';
import { VisitorInfoForm } from './visitor-info-form';

import { ActionsTableMenu } from '@/components/atoms/actions-table-menu';
import { ResourceUnavailable } from '@/components/molecules/resource-unavailable';
import { createEmptyVisitor } from '@/features/visits/utils/create-empty-visitor';
import {
  CreateVisitContext,
  CreateVisitEvent,
} from '@/features/visits/utils/machine/create-visit-machine';
import { useKey } from '@/hooks/use-key';
import { useScrollToTopOnChange } from '@/hooks/use-scroll-to-top-on-change';

interface Props {
  context: CreateVisitContext;
  send: (event: CreateVisitEvent) => void;
}

type VisitorInfoMode = 'list' | 'create' | 'edit';

export const VisitorInfo = ({ context, send }: Props) => {
  const { t } = useTranslation(['common', 'createVisit']);

  const { control, watch, setValue } = useFormContext<CreateVisitFormValues>();

  const visitors = watch('visitors');
  const host = watch('host');
  const allowedToInviteToParking = host?.allowInviteToParking ?? false;
  const discountTemplates = host?.discountTemplates ?? [];

  const { append, fields, remove, update } = useFieldArray({
    control,
    name: 'visitors',
  });

  const [mode, setMode] = useState<VisitorInfoMode>(visitors.length ? 'list' : 'create');
  const [draftVisitor, setDraftVisitor] = useState<CreateVisitVisitorFormValues | null>(
    visitors.length ? null : createEmptyVisitor(),
  );
  const [discountVisitorLocalId, setDiscountVisitorLocalId] = useState<string | null>(null);

  useScrollToTopOnChange(mode);

  const closeVisitorForm = () => {
    setDraftVisitor(null);
    setMode('list');
  };

  const handleAddVisitor = () => {
    if (visitors.length >= MAX_VISITORS) {
      return;
    }

    setDraftVisitor(createEmptyVisitor());
    setMode('create');
  };

  const handleEditVisitor = useCallback(
    (localId: string) => {
      const visitor = visitors.find(visitor => visitor.localId === localId);

      if (!visitor) {
        return;
      }

      setDraftVisitor(visitor);
      setMode('edit');
    },
    [visitors],
  );

  const handleRemoveVisitor = useCallback(
    (localId: string) => {
      const index = fields.findIndex(visitor => visitor.localId === localId);

      if (index === -1) {
        return;
      }

      remove(index);
    },
    [fields, remove],
  );

  const handleSaveVisitor = (visitor: CreateVisitVisitorFormValues) => {
    if (mode === 'create') {
      append(visitor);
      closeVisitorForm();
      return;
    }

    const index = fields.findIndex(field => field.localId === visitor.localId);

    if (index !== -1) {
      update(index, visitor);
    }

    closeVisitorForm();
  };

  const handleAddDiscount = (discountTemplateId: number) => {
    const index = visitors.findIndex(visitor => visitor.localId === discountVisitorLocalId);

    if (
      index === -1 ||
      !visitors[index].hasAccessToParking ||
      !discountTemplates.some(discount => discount.id === discountTemplateId)
    ) {
      return;
    }

    setValue(`visitors.${index}.discountTemplateId`, discountTemplateId, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setDiscountVisitorLocalId(null);
  };

  const actionsSlot = useCallback(
    (localId: string) => {
      const actions = [
        {
          key: 'edit-visitor',
          label: t('common:button.edit'),
          onClick: () => handleEditVisitor(localId),
        },
        {
          key: 'remove-visitor',
          label: t('common:button.remove'),
          onClick: () => handleRemoveVisitor(localId),
        },
      ];

      return <ActionsTableMenu actions={actions} />;
    },
    [handleEditVisitor, handleRemoveVisitor, t],
  );

  const isFormVisible = mode !== 'list' && !!draftVisitor;
  const hasReachedVisitorsLimit = visitors.length >= MAX_VISITORS;
  const canProceedToNextStep =
    mode === 'list' && visitors.length > 0 && discountVisitorLocalId === null;

  const handlePrevStep = () => {
    send({ type: 'BACK' });
  };

  const handleNextStep = () => {
    if (!canProceedToNextStep) {
      return;
    }

    send({ type: 'NEXT' });
  };

  useKey('Enter', handleNextStep, canProceedToNextStep);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          justifyContent: 'space-between',
          my: 2,
        }}>
        <Typography color="textPrimary" variant="h6">
          {t('common:step')} {context.step}. {t(context.content)}
        </Typography>
        <ImportVisitorsDialogButton onImportSuccess={closeVisitorForm} />
      </Box>

      {isFormVisible ? (
        <VisitorInfoForm
          key={draftVisitor.localId}
          allowInviteToParking={allowedToInviteToParking}
          discountTemplates={discountTemplates}
          onCancel={visitors.length ? closeVisitorForm : handlePrevStep}
          onSubmit={handleSaveVisitor}
          visitor={draftVisitor}
        />
      ) : (
        <>
          {visitors.length ? (
            <VisitorsTable
              visitors={visitors}
              discountTemplates={discountTemplates}
              actionsSlot={actionsSlot}
              onAddDiscount={setDiscountVisitorLocalId}
            />
          ) : (
            <ResourceUnavailable text={t('createVisit:validation.visitors')} />
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 'auto',
              pt: 3,
            }}>
            <Button
              color="primary"
              onClick={handlePrevStep}
              size="large"
              sx={{ mr: 2 }}
              type="button"
              variant="text">
              {t('common:button.back')}
            </Button>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'flex-end',
              }}>
              <Tooltip
                title={t('createVisit:validation.maxVisitors', { count: MAX_VISITORS })}
                disableHoverListener={!hasReachedVisitorsLimit}>
                <span>
                  <Button
                    onClick={handleAddVisitor}
                    startIcon={<Add />}
                    type="button"
                    variant="outlined"
                    disabled={hasReachedVisitorsLimit}>
                    {t('createVisit:visitors.addVisitor')}
                  </Button>
                </span>
              </Tooltip>
              <Button
                color="primary"
                size="large"
                type="button"
                variant="contained"
                disabled={!canProceedToNextStep}
                onClick={handleNextStep}>
                {t('common:button.nextStep')}
              </Button>
            </Box>
          </Box>
        </>
      )}
      <AddVisitorDiscountDialog
        open={discountVisitorLocalId !== null}
        discountTemplates={discountTemplates}
        onCancel={() => setDiscountVisitorLocalId(null)}
        onConfirm={handleAddDiscount}
      />
    </Box>
  );
};
